import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import BrandLogo from '../components/BrandLogo';
import brandMark from '../assets/virei-noiva-mark.svg';
import { 
  Heart, CalendarIcon, MapPin, Image, Save, Eye, Check, ArrowLeft,
  Palette, Type, Upload, X, Plus, QrCode, Copy, ExternalLink, Trash2,
  Monitor, Smartphone, RefreshCw, Settings, Layout, Clock, Users, Gift,
  ChevronDown, Camera
} from 'lucide-react';
import { WEDDING_TEMPLATES, resolveTemplateId } from '../lib/weddingTemplates';
import { TEMPLATE_PREVIEW_CONTENT } from '../lib/templatePreviewContent';
import { normalizeObjectStrings } from '../lib/utils';
import { trackEvent } from '../lib/analytics';
import WeddingTemplateRenderer from '../templates/wedding/WeddingTemplateRenderer';
import { EDITOR_TEMPLATE_IDS, isStructuredTemplateId } from '../templates/wedding/structuredTemplateIds';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ACCEPTED_IMAGE_LABEL = 'JPG, PNG, WEBP ou GIF';
const MAX_UPLOAD_MB = 12;
const HTML_EXACT_DECOR_TOP = 'https://cdn0.casamentos.com.br/website/fotos/templates/180_home.png?v=c10dfde9501c0359853ad0dbcf4ab591';
const HTML_EXACT_DECOR_TOP_MOBILE = 'https://cdn0.casamentos.com.br/website/fotos/templates/180_home_mobile.png?v=c10dfde9501c0359853ad0dbcf4ab591';
const HTML_EXACT_SEPARATOR = 'https://cdn0.casamentos.com.br/website/fotos/templates/180_separator.png?v=c10dfde9501c0359853ad0dbcf4ab591';

const extractApiErrorMessage = (error, fallback = 'Erro ao enviar imagem') => {
  const payload = error?.response?.data;
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }
  if (payload?.detail) {
    if (typeof payload.detail === 'string') {
      return payload.detail;
    }
    if (Array.isArray(payload.detail) && payload.detail.length > 0) {
      const first = payload.detail[0];
      if (typeof first?.msg === 'string') {
        return first.msg;
      }
    }
  }
  return fallback;
};

const normalizeSlugInput = (value = '') => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, 'e')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const normalizeHexColor = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toUpperCase();
  }
  return null;
};

const getAutoContrastTextColor = (primaryColor, fallbackColor = '#1A1A1A') => {
  const normalized = normalizeHexColor(primaryColor);
  if (!normalized) return fallbackColor;
  const numeric = Number.parseInt(normalized.slice(1), 16);
  const red = (numeric >> 16) & 255;
  const green = (numeric >> 8) & 255;
  const blue = numeric & 255;
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance > 0.58 ? '#1A1A1A' : '#FFFFFF';
};

const hexToRgba = (hexColor, alpha = 1) => {
  const normalized = normalizeHexColor(hexColor);
  if (!normalized) return `rgba(0,0,0,${alpha})`;
  const numeric = Number.parseInt(normalized.slice(1), 16);
  const red = (numeric >> 16) & 255;
  const green = (numeric >> 8) & 255;
  const blue = numeric & 255;
  const safeAlpha = Math.max(0, Math.min(1, Number(alpha) || 0));
  return `rgba(${red}, ${green}, ${blue}, ${safeAlpha})`;
};
// Font options
const FONT_OPTIONS = [
  { id: 'playfair', name: 'Playfair Display', family: "'Playfair Display', serif" },
  { id: 'dancing', name: 'Dancing Script', family: "'Dancing Script', cursive" },
  { id: 'great-vibes', name: 'Great Vibes', family: "'Great Vibes', cursive" },
  { id: 'cormorant', name: 'Cormorant Garamond', family: "'Cormorant Garamond', serif" },
  { id: 'lora', name: 'Lora', family: "'Lora', serif" },
  { id: 'montserrat', name: 'Montserrat', family: "'Montserrat', sans-serif" },
  { id: 'raleway', name: 'Raleway', family: "'Raleway', sans-serif" },
  { id: 'josefin', name: 'Josefin Sans', family: "'Josefin Sans', sans-serif" },
  { id: 'mulish', name: 'Mulish', family: "'Mulish', sans-serif" },
  { id: 'eb-garamond', name: 'EB Garamond', family: "'EB Garamond', serif" },
  { id: 'didact', name: 'Didact Gothic', family: "'Didact Gothic', sans-serif" },
  { id: 'poiret', name: 'Poiret One', family: "'Poiret One', cursive" },
  { id: 'syne', name: 'Syne', family: "'Syne', sans-serif" },
  { id: 'jost', name: 'Jost', family: "'Jost', sans-serif" },
  { id: 'spectral', name: 'Spectral', family: "'Spectral', serif" },
  { id: 'italiana', name: 'Italiana', family: "'Italiana', serif" },
  { id: 'karla', name: 'Karla', family: "'Karla', sans-serif" },
  { id: 'anton', name: 'Anton', family: "'Anton', sans-serif" },
  { id: 'dm-sans', name: 'DM Sans', family: "'DM Sans', sans-serif" },
  { id: 'noto-serif-jp', name: 'Noto Serif JP', family: "'Noto Serif JP', serif" },
  { id: 'source-sans-3', name: 'Source Sans 3', family: "'Source Sans 3', sans-serif" },
  { id: 'merriweather', name: 'Merriweather', family: "'Merriweather', serif" },
  { id: 'lato', name: 'Lato', family: "'Lato', sans-serif" },
  { id: 'libre-baskerville', name: 'Libre Baskerville', family: "'Libre Baskerville', serif" }
];

// Layout options
const LAYOUT_OPTIONS = [
  { id: 'classic', name: 'Clássico', description: 'Hero grande, seções empilhadas' },
  { id: 'modern', name: 'Moderno', description: 'Minimalista com espaçamento' },
  { id: 'elegant', name: 'Elegante', description: 'Detalhes decorativos' },
  { id: 'romantic', name: 'Romântico', description: 'Curvas e tons suaves' }
];

const TEMPLATE_EDITOR_PRESETS = {
  'classic-editorial': {
    heading_font: 'playfair',
    body_font: 'montserrat',
    layout_style: 'elegant',
    background_color: '#FAF8F2',
    text_color: '#2F2E2E',
    hero_overlay_opacity: 42
  },
  'botanical-garden': {
    heading_font: 'cormorant',
    body_font: 'lora',
    layout_style: 'romantic',
    background_color: '#F4F8F5',
    text_color: '#2E5243',
    hero_overlay_opacity: 34
  },
  'modern-black-tie': {
    heading_font: 'montserrat',
    body_font: 'montserrat',
    layout_style: 'modern',
    background_color: '#11131B',
    text_color: '#E6E8EC',
    hero_overlay_opacity: 58
  },
  'destination-praia': {
    heading_font: 'lora',
    body_font: 'raleway',
    layout_style: 'classic',
    background_color: '#F0F7FB',
    text_color: '#34566A',
    hero_overlay_opacity: 22
  },
  'minimal-classic': {
    heading_font: 'playfair',
    body_font: 'raleway',
    layout_style: 'classic',
    background_color: '#F8F8F8',
    text_color: '#2F2E2E',
    hero_overlay_opacity: 18
  },
  'vintage-retro': {
    heading_font: 'playfair',
    body_font: 'raleway',
    layout_style: 'classic',
    background_color: '#FAF5EA',
    text_color: '#704214',
    hero_overlay_opacity: 0
  },
  'scandinavian-minimal': {
    heading_font: 'josefin',
    body_font: 'josefin',
    layout_style: 'modern',
    background_color: '#FAFAFA',
    text_color: '#1A1A1A',
    hero_overlay_opacity: 0
  },
  'por-do-sol': {
    heading_font: 'cormorant',
    body_font: 'mulish',
    layout_style: 'romantic',
    background_color: '#FFF9F5',
    text_color: '#1A0A04',
    hero_overlay_opacity: 0
  },
  'noir-dorado': {
    heading_font: 'playfair',
    body_font: 'montserrat',
    layout_style: 'classic',
    background_color: '#F7F0E3',
    text_color: '#0A0804',
    hero_overlay_opacity: 0
  },
  'terra-flores': {
    heading_font: 'playfair',
    body_font: 'lora',
    layout_style: 'romantic',
    background_color: '#FAF6EE',
    text_color: '#8B6347',
    hero_overlay_opacity: 0
  },
  'linha-pura': {
    heading_font: 'josefin',
    body_font: 'raleway',
    layout_style: 'modern',
    background_color: '#FAFAFA',
    text_color: '#0D0D0D',
    hero_overlay_opacity: 0
  },
  'jardim-secreto': {
    heading_font: 'eb-garamond',
    body_font: 'raleway',
    layout_style: 'romantic',
    background_color: '#FBF7EF',
    text_color: '#1C2E1A',
    hero_overlay_opacity: 0
  },
  'art-deco': {
    heading_font: 'poiret',
    body_font: 'didact',
    layout_style: 'classic',
    background_color: '#1A1A14',
    text_color: '#0C0C08',
    hero_overlay_opacity: 0
  },
  'praia-brisa': {
    heading_font: 'syne',
    body_font: 'lora',
    layout_style: 'classic',
    background_color: '#FAF6F0',
    text_color: '#1B4F72',
    hero_overlay_opacity: 0
  },
  'inverno-real': {
    heading_font: 'spectral',
    body_font: 'jost',
    layout_style: 'classic',
    background_color: '#F8FBFF',
    text_color: '#0D1F3C',
    hero_overlay_opacity: 0
  },
  'veludo-rosa': {
    heading_font: 'italiana',
    body_font: 'mulish',
    layout_style: 'romantic',
    background_color: '#FAF5F6',
    text_color: '#8B2252',
    hero_overlay_opacity: 0
  },
  'tipo-gigante': {
    heading_font: 'anton',
    body_font: 'karla',
    layout_style: 'modern',
    background_color: '#FFFFFF',
    text_color: '#0A0A0A',
    hero_overlay_opacity: 0
  },
  'sakura': {
    heading_font: 'noto-serif-jp',
    body_font: 'dm-sans',
    layout_style: 'romantic',
    background_color: '#FAF7F5',
    text_color: '#1A0A0A',
    hero_overlay_opacity: 0
  },
  'serra-nevoa': {
    heading_font: 'merriweather',
    body_font: 'source-sans-3',
    layout_style: 'classic',
    background_color: '#FAFAF8',
    text_color: '#4A5568',
    hero_overlay_opacity: 0
  },
  'parisiense': {
    heading_font: 'cormorant',
    body_font: 'lato',
    layout_style: 'classic',
    background_color: '#F8F5F0',
    text_color: '#2C2C2C',
    hero_overlay_opacity: 0
  },
  'tropical': {
    heading_font: 'playfair',
    body_font: 'dm-sans',
    layout_style: 'classic',
    background_color: '#FAF9F5',
    text_color: '#1A4731',
    hero_overlay_opacity: 0
  },
  'prata-eterno': {
    heading_font: 'josefin',
    body_font: 'josefin',
    layout_style: 'classic',
    background_color: '#F2F2F2',
    text_color: '#1A1A1A',
    hero_overlay_opacity: 0
  },
  'bohemia': {
    heading_font: 'libre-baskerville',
    body_font: 'source-sans-3',
    layout_style: 'classic',
    background_color: '#FBF6EE',
    text_color: '#C4622D',
    hero_overlay_opacity: 0
  },
  'noite-estrelada': {
    heading_font: 'cormorant',
    body_font: 'jost',
    layout_style: 'classic',
    background_color: '#0F1628',
    text_color: '#0A0E1A',
    hero_overlay_opacity: 0
  },
  'azulejo-portugues': {
    heading_font: 'merriweather',
    body_font: 'source-sans-3',
    layout_style: 'classic',
    background_color: '#F7F5F0',
    text_color: '#1B4B82',
    hero_overlay_opacity: 0
  },
  'ethereal-grid': {
    heading_font: 'montserrat',
    body_font: 'montserrat',
    layout_style: 'classic',
    background_color: '#FFFFFF',
    text_color: '#F5F5F4',
    hero_overlay_opacity: 38,
    show_countdown: false
  },
  'premium-wedding-experience': {
    heading_font: 'montserrat',
    body_font: 'montserrat',
    layout_style: 'elegant',
    background_color: '#FAF8F2',
    text_color: '#F3EEE8',
    hero_overlay_opacity: 32
  }
};

const SECTION_STYLE_OVERRIDE_FIELDS = [
  'banner_title_color',
  'banner_title_font_family',
  'banner_text_color',
  'banner_text_font_family',
  'banner_section_background_color',
  'confirm_button_background_color',
  'confirm_button_text_color',
  'confirm_button_border_color',
  'story_title_color',
  'story_title_font_family',
  'story_text_font_family',
  'story_section_background_color',
  'gallery_title_color',
  'gallery_icon_color',
  'gallery_section_background_color',
  'gift_title_color',
  'gift_text_color',
  'gift_icon_color',
  'gift_section_background_color',
  'gift_card_background_color',
  'gift_price_color',
  'gift_button_background_color',
  'gift_button_text_color',
  'location_title_color',
  'location_title_font_family',
  'location_text_color',
  'location_text_font_family',
  'location_section_background_color',
  'location_card_background_color',
  'location_icon_color',
  'rsvp_section_background_color',
  'rsvp_text_color',
  'rsvp_title_font_family',
  'rsvp_text_font_family',
  'rsvp_icon_color'
];

export default function SiteEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [site, setSite] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [siteGifts, setSiteGifts] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showQRModal, setShowQRModal] = useState(false);
  const [styleAccordionOpen, setStyleAccordionOpen] = useState({
    banner: true,
    story: false,
    gallery: false,
    gifts: false,
    location: false,
    rsvp: false
  });
  const [slugInput, setSlugInput] = useState('');
  const [updatingSlug, setUpdatingSlug] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const heroInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const previewRef = useRef(null);
  const templateSelectionAppliedRef = useRef(false);

  // Form state with extended options
  const [formData, setFormData] = useState({
    couple_names: '',
    wedding_date: new Date(),
    template_id: 'ivory-classic',
    primary_color: '#1C1C1C',
    secondary_color: '#C8A97E',
    background_color: '#F6F1EF',
    text_color: '#1a1a1a',
    banner_title_color: '',
    banner_title_font_family: '',
    banner_text_color: '',
    banner_text_font_family: '',
    banner_section_background_color: '',
    confirm_button_background_color: '',
    confirm_button_text_color: '',
    confirm_button_border_color: '',
    story_title_color: '',
    story_title_font_family: '',
    story_text_font_family: '',
    story_section_background_color: '',
    gallery_title_color: '',
    gallery_icon_color: '',
    gallery_section_background_color: '',
    gift_title_color: '',
    gift_text_color: '',
    gift_icon_color: '',
    gift_section_background_color: '',
    gift_card_background_color: '',
    gift_price_color: '',
    gift_button_background_color: '',
    gift_button_text_color: '',
    location_title_color: '',
    location_title_font_family: '',
    location_text_color: '',
    location_text_font_family: '',
    location_section_background_color: '',
    location_card_background_color: '',
    location_icon_color: '',
    rsvp_section_background_color: '',
    rsvp_text_color: '',
    rsvp_title_font_family: '',
    rsvp_text_font_family: '',
    rsvp_icon_color: '',
    heading_font: 'playfair',
    body_font: 'montserrat',
    layout_style: 'classic',
    hero_image: '',
    hero_overlay_opacity: 70,
    gallery_images: [],
    gallery_position: 'after_story',
    story_text: '',
    ceremony_location: '',
    ceremony_address: '',
    ceremony_time: '',
    same_location: false,
    reception_location: '',
    reception_address: '',
    reception_time: '',
    show_countdown: true,
    show_rsvp_count: true,
    show_gift_section: true,
    card_fee_responsibility: 'payer',
    is_published: false
  });

  useEffect(() => {
    fetchData();
    // Load Google Fonts
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Anton&family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;700&family=Dancing+Script:wght@400;700&family=Didact+Gothic&family=EB+Garamond:wght@400;500;600;700&family=Great+Vibes&family=Inter:wght@600&family=Italiana&family=Josefin+Sans:wght@200;300;400;500;600;700&family=Jost:wght@300;400;500;600;700&family=Karla:wght@300;400;500;700&family=Lato:wght@300;400;700&family=Libre+Baskerville:wght@400;700&family=Lora:wght@400;600;700&family=Merriweather:wght@300;400;700&family=Montserrat:wght@400;500;600;700&family=Mulish:wght@200;300;400;500;600;700&family=Noto+Serif+JP:wght@300;400;600;700&family=Playfair+Display:wght@400;600;700&family=Poiret+One&family=Raleway:wght@400;500;600;700&family=Source+Sans+3:wght@300;400;600;700&family=Spectral:wght@300;400;500;600;700&family=Syne:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const requestedTemplateParam = searchParams.get('template') || sessionStorage.getItem('selectedTemplateId') || '';
  const requestedTemplateId = requestedTemplateParam ? resolveTemplateId(requestedTemplateParam) : '';

  const applyTemplateSelection = (prevState, templateId) => {
    const resolvedId = resolveTemplateId(templateId);
    const selectedTemplate = WEDDING_TEMPLATES.find((template) => template.id === resolvedId);
    if (!selectedTemplate) {
      return prevState;
    }

    const preset = TEMPLATE_EDITOR_PRESETS[resolvedId] || {};
    const resetSectionOverrides = SECTION_STYLE_OVERRIDE_FIELDS.reduce((acc, field) => {
      acc[field] = '';
      return acc;
    }, {});
    const nextState = {
      ...prevState,
      ...resetSectionOverrides,
      ...preset,
      template_id: resolvedId,
      primary_color: selectedTemplate.colors?.primary || prevState.primary_color,
      secondary_color: selectedTemplate.colors?.secondary || prevState.secondary_color,
    };

    if (!prevState.hero_image) {
      nextState.hero_image = selectedTemplate.preview_image || prevState.hero_image;
    }

    if (!prevState.text_color && preset.text_color) {
      nextState.text_color = preset.text_color;
    }

    return nextState;
  };

  const fetchData = async () => {
    try {
      const [siteRes, templatesRes, giftsRes] = await Promise.all([
        axios.get(`${API}/sites/my`, { withCredentials: true }),
        axios.get(`${API}/templates`, { withCredentials: true }),
        axios.get(`${API}/gifts`, { withCredentials: true })
      ]);
      
      setTemplates(normalizeObjectStrings(templatesRes.data || []));
      setSiteGifts(normalizeObjectStrings(giftsRes.data || []));
      
      if (siteRes.data) {
        const normalizedSite = normalizeObjectStrings(siteRes.data);
        setSite(normalizedSite);
        setSlugInput(normalizedSite.slug || '');
        setFormData(prev => ({
          ...prev,
          ...normalizedSite,
          template_id: resolveTemplateId(normalizedSite.template_id),
          wedding_date: new Date(normalizedSite.wedding_date),
          gallery_images: normalizedSite.gallery_images || [],
          heading_font: normalizedSite.heading_font || 'playfair',
          body_font: normalizedSite.body_font || 'montserrat',
          layout_style: normalizedSite.layout_style || 'classic',
          hero_overlay_opacity: normalizedSite.hero_overlay_opacity != null ? normalizedSite.hero_overlay_opacity : 70,
          gallery_position: normalizedSite.gallery_position ? normalizedSite.gallery_position : 'after_story',
          same_location: Boolean(normalizedSite.same_location),
          show_countdown: normalizedSite.show_countdown !== false,
          show_rsvp_count: normalizedSite.show_rsvp_count !== false,
          show_gift_section: normalizedSite.show_gift_section !== false,
          card_fee_responsibility: normalizedSite.card_fee_responsibility === 'couple' ? 'couple' : 'payer',
          banner_title_color: normalizedSite.banner_title_color || '',
          banner_title_font_family: normalizedSite.banner_title_font_family || '',
          banner_text_color: normalizedSite.banner_text_color || '',
          banner_text_font_family: normalizedSite.banner_text_font_family || '',
          banner_section_background_color: normalizedSite.banner_section_background_color || '',
          confirm_button_background_color: normalizedSite.confirm_button_background_color || '',
          confirm_button_text_color: normalizedSite.confirm_button_text_color || '',
          confirm_button_border_color: normalizedSite.confirm_button_border_color || '',
          story_title_color: normalizedSite.story_title_color || '',
          story_title_font_family: normalizedSite.story_title_font_family || '',
          story_text_font_family: normalizedSite.story_text_font_family || '',
          story_section_background_color: normalizedSite.story_section_background_color || '',
          gallery_title_color: normalizedSite.gallery_title_color || '',
          gallery_icon_color: normalizedSite.gallery_icon_color || '',
          gallery_section_background_color: normalizedSite.gallery_section_background_color || '',
          gift_title_color: normalizedSite.gift_title_color || '',
          gift_text_color: normalizedSite.gift_text_color || '',
          gift_icon_color: normalizedSite.gift_icon_color || '',
          gift_section_background_color: normalizedSite.gift_section_background_color || '',
          gift_card_background_color: normalizedSite.gift_card_background_color || '',
          gift_price_color: normalizedSite.gift_price_color || '',
          gift_button_background_color: normalizedSite.gift_button_background_color || '',
          gift_button_text_color: normalizedSite.gift_button_text_color || '',
          location_title_color: normalizedSite.location_title_color || '',
          location_title_font_family: normalizedSite.location_title_font_family || '',
          location_text_color: normalizedSite.location_text_color || '',
          location_text_font_family: normalizedSite.location_text_font_family || '',
          location_section_background_color: normalizedSite.location_section_background_color || '',
          location_card_background_color: normalizedSite.location_card_background_color || '',
          location_icon_color: normalizedSite.location_icon_color || '',
          rsvp_section_background_color: normalizedSite.rsvp_section_background_color || '',
          rsvp_text_color: normalizedSite.rsvp_text_color || '',
          rsvp_title_font_family: normalizedSite.rsvp_title_font_family || '',
          rsvp_text_font_family: normalizedSite.rsvp_text_font_family || '',
          rsvp_icon_color: normalizedSite.rsvp_icon_color || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading || templateSelectionAppliedRef.current || !requestedTemplateId) {
      return;
    }

    setFormData((prev) => applyTemplateSelection(prev, requestedTemplateId));
    templateSelectionAppliedRef.current = true;
    sessionStorage.removeItem('selectedTemplateId');
    toast.success('Template aplicado ao editor.');
  }, [loading, requestedTemplateId]);

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    const fileType = (file.type || '').toLowerCase();
    if (!ACCEPTED_IMAGE_TYPES.includes(fileType)) {
      toast.error(`Formato inválido. Use ${ACCEPTED_IMAGE_LABEL}.`);
      return;
    }

    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`Imagem muito grande. Limite de ${MAX_UPLOAD_MB}MB.`);
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${API}/images/${response.data.file_id}`;
      
      if (type === 'hero') {
        setFormData(prev => ({ ...prev, hero_image: imageUrl }));
      } else if (type === 'gallery') {
        setFormData(prev => ({ 
          ...prev, 
          gallery_images: [...prev.gallery_images, imageUrl] 
        }));
      }
      
      toast.success('Imagem enviada!');
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error('Sua sessão expirou. Faça login novamente.');
        navigate('/login', { replace: true });
        return;
      }
      toast.error(extractApiErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleHeroFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    handleImageUpload(selectedFile, 'hero');
  };

  const handleGalleryFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    handleImageUpload(selectedFile, 'gallery');
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        wedding_date: formData.wedding_date.toISOString(),
        template_id: resolveTemplateId(formData.template_id)
      };

      if (site) {
        await axios.put(`${API}/sites/${site.site_id}`, payload, { withCredentials: true });
        toast.success('Site atualizado!');
      } else {
        trackEvent('wedding_site_create_start', {
          template_id: payload.template_id,
          slug: payload.slug || undefined,
        });
        const response = await axios.post(`${API}/sites`, payload, { withCredentials: true });
        const normalizedSite = normalizeObjectStrings(response.data);
        setSite(normalizedSite);
        setSlugInput(normalizedSite.slug || '');
        trackEvent('wedding_site_created', {
          site_id: normalizedSite.site_id,
          template_id: normalizedSite.template_id || payload.template_id,
          slug: normalizedSite.slug || undefined,
        });
        toast.success('Site criado!');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleSlugUpdate = async () => {
    if (!site?.site_id) {
      toast.error('Salve o site primeiro para personalizar o endereço.');
      return;
    }

    const normalizedSlug = normalizeSlugInput(slugInput);
    if (!normalizedSlug) {
      toast.error('Informe um endereço válido com letras, números e hífens.');
      return;
    }

    if (normalizedSlug === site.slug) {
      setSlugInput(normalizedSlug);
      toast.info('Esse endereço já está aplicado.');
      return;
    }

    setUpdatingSlug(true);
    try {
      const response = await axios.put(
        `${API}/sites/${site.site_id}/slug`,
        { slug: normalizedSlug },
        { withCredentials: true }
      );

      const updatedSlug = response.data?.slug || normalizedSlug;
      setSlugInput(updatedSlug);
      setSite(prev => (prev ? { ...prev, slug: updatedSlug } : prev));
      toast.success('Endereço do site atualizado!');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409) {
        toast.error(detail || 'Esse endereço já está em uso.');
      } else {
        toast.error(detail || 'Erro ao atualizar endereço');
      }
    } finally {
      setUpdatingSlug(false);
    }
  };
  const copyLink = () => {
    if (!site?.slug) {
      toast.error('Salve o site primeiro para copiar o link');
      return;
    }
    const url = `${window.location.origin}/s/${site.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };

  const getSiteUrl = () => site?.slug ? `${window.location.origin}/s/${site.slug}` : '';
  
  const getHeadingFont = () => FONT_OPTIONS.find(f => f.id === formData.heading_font)?.family || FONT_OPTIONS[0].family;
  const getBodyFont = () => FONT_OPTIONS.find(f => f.id === formData.body_font)?.family || FONT_OPTIONS[5].family;
  const getFontFamily = (fontId, fallback) => FONT_OPTIONS.find((font) => font.id === fontId)?.family || fallback;
  const templateSource = templates.length > 0 ? templates : WEDDING_TEMPLATES;
  const baseTemplateOptions = templateSource.filter((template) => EDITOR_TEMPLATE_IDS.includes(template.id));
  const selectedTemplateId = resolveTemplateId(formData.template_id);
  const selectedTemplateOption = templateSource.find((template) => template.id === selectedTemplateId);
  const templateOptions =
    selectedTemplateOption && !EDITOR_TEMPLATE_IDS.includes(selectedTemplateId)
      ? [...baseTemplateOptions, selectedTemplateOption]
      : baseTemplateOptions;
  const selectedTemplateForUI = templateOptions.some((template) => template.id === selectedTemplateId)
    ? selectedTemplateId
    : EDITOR_TEMPLATE_IDS[0];
  const isClassicEditorialTemplate = selectedTemplateId === 'classic-editorial';
  const isBotanicalGardenTemplate = selectedTemplateId === 'botanical-garden';
  const isModernBlackTieTemplate = selectedTemplateId === 'modern-black-tie';
  const isDestinationPraiaTemplate = selectedTemplateId === 'destination-praia';
  const isMinimalClassicTemplate = selectedTemplateId === 'minimal-classic';
  const isPremiumWeddingTemplate = selectedTemplateId === 'premium-wedding-experience';
  const isHtmlExactTemplate = selectedTemplateId === 'ethereal-grid';
  const previewTextColor = normalizeHexColor(formData.text_color) || '#333333';
  const previewBannerTitleColor = normalizeHexColor(formData.banner_title_color) || previewTextColor;
  const previewBannerTextColor = normalizeHexColor(formData.banner_text_color) || previewTextColor;
  const previewBannerSectionBackgroundColor = normalizeHexColor(formData.banner_section_background_color) || '';
  const previewBannerTitleFont = FONT_OPTIONS.find((font) => font.id === formData.banner_title_font_family)?.family || getHeadingFont();
  const previewBannerTextFont = getFontFamily(formData.banner_text_font_family, getBodyFont());
  const previewConfirmButtonBackgroundColor = normalizeHexColor(formData.confirm_button_background_color) || formData.primary_color;
  const previewConfirmButtonTextColor = normalizeHexColor(formData.confirm_button_text_color) || getAutoContrastTextColor(previewConfirmButtonBackgroundColor, '#FFFFFF');
  const previewConfirmButtonBorderColor = normalizeHexColor(formData.confirm_button_border_color) || previewConfirmButtonBackgroundColor;
  const previewStoryTitleColor = normalizeHexColor(formData.story_title_color) || (isHtmlExactTemplate ? formData.primary_color : previewTextColor);
  const previewStoryTitleFont = FONT_OPTIONS.find((font) => font.id === formData.story_title_font_family)?.family || getHeadingFont();
  const previewStoryBodyColor = normalizeHexColor(formData.story_title_color) || (isHtmlExactTemplate ? '#4B5563' : previewTextColor);
  const previewStoryBodyFont = getFontFamily(formData.story_text_font_family, getBodyFont());
  const previewStorySectionBackgroundColor = normalizeHexColor(formData.story_section_background_color) || '';
  const previewGalleryTitleColor = normalizeHexColor(formData.gallery_title_color) || formData.primary_color;
  const previewGalleryIconColor = normalizeHexColor(formData.gallery_icon_color) || formData.secondary_color;
  const previewGallerySectionBackgroundColor = normalizeHexColor(formData.gallery_section_background_color) || '#FFFFFF';
  const previewGiftTitleColor = normalizeHexColor(formData.gift_title_color) || formData.primary_color;
  const previewGiftTextColor = normalizeHexColor(formData.gift_text_color) || previewTextColor;
  const previewGiftIconColor = normalizeHexColor(formData.gift_icon_color) || formData.secondary_color;
  const previewGiftSectionBackgroundColor = normalizeHexColor(formData.gift_section_background_color) || '#FFFFFF';
  const previewGiftCardBackgroundColor = normalizeHexColor(formData.gift_card_background_color) || '#FFFFFF';
  const previewGiftPriceColor = normalizeHexColor(formData.gift_price_color) || formData.secondary_color;
  const previewGiftButtonBackgroundColor = normalizeHexColor(formData.gift_button_background_color) || formData.primary_color;
  const previewGiftButtonTextColor = normalizeHexColor(formData.gift_button_text_color) || getAutoContrastTextColor(previewGiftButtonBackgroundColor, '#FFFFFF');
  const previewLocationTitleColor = normalizeHexColor(formData.location_title_color) || formData.primary_color;
  const previewLocationTextColor = normalizeHexColor(formData.location_text_color) || previewTextColor;
  const previewLocationTitleFont = getFontFamily(formData.location_title_font_family, getHeadingFont());
  const previewLocationTextFont = getFontFamily(formData.location_text_font_family, getBodyFont());
  const previewLocationSectionBackgroundColor = normalizeHexColor(formData.location_section_background_color) || '';
  const previewLocationCardBackgroundColor = normalizeHexColor(formData.location_card_background_color) || '';
  const previewLocationIconColor = normalizeHexColor(formData.location_icon_color) || previewLocationTitleColor;
  const previewRsvpSectionBackgroundColor = normalizeHexColor(formData.rsvp_section_background_color) || '';
  const previewRsvpTextColor = normalizeHexColor(formData.rsvp_text_color) || previewTextColor;
  const previewRsvpTitleFont = getFontFamily(formData.rsvp_title_font_family, getHeadingFont());
  const previewRsvpTextFont = getFontFamily(formData.rsvp_text_font_family, getBodyFont());
  const previewRsvpIconColor = normalizeHexColor(formData.rsvp_icon_color) || previewRsvpTextColor;
  const previewHeroOverlayTint = hexToRgba(formData.primary_color, Math.max(0, Math.min(100, Number(formData.hero_overlay_opacity ?? 70))) / 100);
  const previewDisplayDate = formData.wedding_date || new Date(`${TEMPLATE_PREVIEW_CONTENT.wedding_date}T12:00:00`);
  const previewCoupleNames = (formData.couple_names || '').trim() || TEMPLATE_PREVIEW_CONTENT.couple_names;
  const previewStoryText = (formData.story_text || '').trim();
  const previewHeroImage = (formData.hero_image || '').trim() || TEMPLATE_PREVIEW_CONTENT.hero_image;
  const previewGalleryImages = formData.gallery_images?.length > 0 ? formData.gallery_images : TEMPLATE_PREVIEW_CONTENT.gallery_images;
  const previewCeremonyLocation = (formData.ceremony_location || '').trim() || TEMPLATE_PREVIEW_CONTENT.ceremony_location;
  const previewCeremonyAddress = (formData.ceremony_address || '').trim() || TEMPLATE_PREVIEW_CONTENT.ceremony_address;
  const previewCeremonyTime = (formData.ceremony_time || '').trim() || TEMPLATE_PREVIEW_CONTENT.ceremony_time;
  const previewReceptionLocation = (formData.reception_location || '').trim() || TEMPLATE_PREVIEW_CONTENT.reception_location;
  const previewReceptionAddress = (formData.reception_address || '').trim() || TEMPLATE_PREVIEW_CONTENT.reception_address;
  const previewReceptionTime = (formData.reception_time || '').trim() || TEMPLATE_PREVIEW_CONTENT.reception_time;

  // Calculate days until wedding
  const daysUntil = Math.max(0, Math.ceil((previewDisplayDate - new Date()) / (1000 * 60 * 60 * 24)));
  const previewGifts = siteGifts.filter(gift => gift.is_available).slice(0, 3);
  const previewDisplayGifts = previewGifts.length > 0 ? previewGifts : TEMPLATE_PREVIEW_CONTENT.gifts;
  const previewCardFeeResponsibility = formData.card_fee_responsibility === 'couple' ? 'couple' : 'payer';
  const previewGalleryPosition = ['after_hero', 'after_story', 'before_gifts', 'end'].includes(formData.gallery_position)
    ? formData.gallery_position
    : 'after_story';
  const previewGallerySection = previewGalleryImages.length > 0 ? (
    <section className="py-12 px-6" style={{ background: previewGallerySectionBackgroundColor }}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Camera className="w-8 h-8 mx-auto mb-3" style={{ color: previewGalleryIconColor }} />
          <h2
            className="text-2xl font-bold"
            style={{ fontFamily: getHeadingFont(), color: previewGalleryTitleColor }}
          >
            Nossa Galeria
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {previewGalleryImages.slice(0, 6).map((img, idx) => (
            <div key={idx} className="aspect-square rounded-lg overflow-hidden">
              <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    </section>
  ) : null;
  const previewHtmlDivider = isHtmlExactTemplate ? (
    <div
      className="h-14 md:h-20 bg-center bg-repeat-x bg-[length:auto_100%]"
      style={{ backgroundImage: `url(${HTML_EXACT_SEPARATOR})` }}
    />
  ) : null;

  const ceremonyLocation = previewCeremonyLocation;
  const ceremonyAddress = previewCeremonyAddress;
  const ceremonyTime = previewCeremonyTime;
  const receptionLocation = previewReceptionLocation;
  const receptionAddress = previewReceptionAddress;
  const receptionTime = previewReceptionTime;
  const htmlExactDateLabel = format(previewDisplayDate, 'd MMMM yyyy', { locale: ptBR });
  const htmlExactPlaceLabel = (
    ceremonyLocation ||
    receptionLocation ||
    ceremonyAddress ||
    receptionAddress ||
    ''
  ).trim();
  const previewHtmlHeaderPanel = isHtmlExactTemplate ? (
    <div className="bg-white text-center px-6 pb-8">
      <div
        className="h-16 md:h-24 w-full bg-center bg-no-repeat bg-[length:100%_100%]"
        style={{
          backgroundImage: `url(${previewMode === 'mobile' ? HTML_EXACT_DECOR_TOP_MOBILE : HTML_EXACT_DECOR_TOP})`
        }}
      />
      <p className="mt-4 text-[0.72rem] tracking-[0.24em] uppercase text-[#736A60] font-medium">
        Vamos casar!
      </p>
      <p
        className="mt-2 text-xl md:text-2xl text-[#3A3732]"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {htmlExactDateLabel}
      </p>
      {htmlExactPlaceLabel ? (
        <p className="mt-2 text-sm md:text-base text-[#5E5A54]">
          {htmlExactPlaceLabel}
        </p>
      ) : null}
    </div>
  ) : null;
  const inferredSameLocation = Boolean(
    ceremonyLocation &&
      receptionLocation &&
      ceremonyLocation.toLowerCase() === receptionLocation.toLowerCase() &&
      ceremonyAddress.toLowerCase() === receptionAddress.toLowerCase()
  );
  const previewSameLocation = Boolean(formData.same_location || inferredSameLocation);
  const previewLocationCards = previewSameLocation
    ? [
        {
          key: 'combined',
          title: 'Cerimônia e Recepção',
          icon: MapPin,
          iconBackground: formData.secondary_color,
          location: ceremonyLocation || receptionLocation,
          address: ceremonyAddress || receptionAddress,
          time: ceremonyTime || receptionTime
        }
      ]
    : [
        ceremonyLocation
          ? {
              key: 'ceremony',
              title: 'Cerimônia',
              icon: Heart,
              iconBackground: formData.primary_color,
              location: ceremonyLocation,
              address: ceremonyAddress,
              time: ceremonyTime
            }
          : null,
        receptionLocation
          ? {
              key: 'reception',
              title: 'Recepção',
              icon: Users,
              iconBackground: formData.secondary_color,
              location: receptionLocation,
              address: receptionAddress,
              time: receptionTime
            }
          : null
      ].filter(Boolean);

  const useStructuredPreview = isStructuredTemplateId(selectedTemplateId);
  const previewWeddingDateLabel = format(previewDisplayDate, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  const previewStructuredSite = {
    couple_names: previewCoupleNames,
    wedding_date: previewDisplayDate?.toISOString?.() || new Date(`${TEMPLATE_PREVIEW_CONTENT.wedding_date}T12:00:00`).toISOString(),
    story_text: previewStoryText,
    hero_image: previewHeroImage,
    gallery_images: previewGalleryImages,
    ceremony_location: previewCeremonyLocation,
    ceremony_address: previewCeremonyAddress,
    ceremony_time: previewCeremonyTime,
    reception_location: previewReceptionLocation,
    reception_address: previewReceptionAddress,
    reception_time: previewReceptionTime,
    same_location: formData.same_location || TEMPLATE_PREVIEW_CONTENT.same_location,
    show_countdown: formData.show_countdown,
    show_rsvp_count: formData.show_rsvp_count,
    show_gift_section: formData.show_gift_section,
    card_fee_responsibility: previewCardFeeResponsibility,
    confirmed_guests: TEMPLATE_PREVIEW_CONTENT.confirmed_guests
  };
  const previewStructuredTheme = {
    primaryColor: formData.primary_color,
    secondaryColor: formData.secondary_color,
    bodyTextColor: previewTextColor,
    headingFont: getHeadingFont(),
    bannerTitleFont: previewBannerTitleFont,
    bannerTextFont: previewBannerTextFont,
    bannerTextColor: previewBannerTextColor,
    bannerSectionBackground: previewBannerSectionBackgroundColor,
    storyTitleFont: previewStoryTitleFont,
    storyBodyFont: previewStoryBodyFont,
    locationTitleColor: previewLocationTitleColor,
    locationTitleFont: previewLocationTitleFont,
    locationTextColor: previewLocationTextColor,
    locationTextFont: previewLocationTextFont,
    heroImage: previewHeroImage,
    heroOverlay: `linear-gradient(to bottom, ${formData.primary_color}99, ${formData.primary_color}cc)`,
    heroOverlayOpacity: Math.max(0, Math.min(100, Number(formData.hero_overlay_opacity ?? 70))) / 100,
    heroTitleColor: previewBannerTitleColor,
    heroDateColor: previewTextColor,
    storyTitleColor: previewStoryTitleColor,
    storyBodyColor: previewStoryBodyColor,
    storySectionBackground: previewStorySectionBackgroundColor,
    galleryTitleColor: previewGalleryTitleColor,
    galleryIconColor: previewGalleryIconColor,
    gallerySectionBackground: previewGallerySectionBackgroundColor,
    giftTitleColor: previewGiftTitleColor,
    giftTextColor: previewGiftTextColor,
    giftIconColor: previewGiftIconColor,
    giftSectionBackground: previewGiftSectionBackgroundColor,
    giftCardBackground: previewGiftCardBackgroundColor,
    giftPriceColor: previewGiftPriceColor,
    giftButtonBackground: previewGiftButtonBackgroundColor,
    giftButtonText: previewGiftButtonTextColor,
    locationSectionBackground: previewLocationSectionBackgroundColor,
    locationCardBackground: previewLocationCardBackgroundColor,
    locationIconColor: previewLocationIconColor,
    rsvpSectionBackground: previewRsvpSectionBackgroundColor,
    rsvpTextColor: previewRsvpTextColor,
    rsvpTitleFont: previewRsvpTitleFont,
    rsvpTextFont: previewRsvpTextFont,
    rsvpIconColor: previewRsvpIconColor,
    confirmButtonBackground: previewConfirmButtonBackgroundColor,
    confirmButtonText: previewConfirmButtonTextColor,
    confirmButtonBorder: previewConfirmButtonBorderColor
  };
  const renderFontSelectField = (label, field, fallbackLabel = 'Usar fonte padrao') => (
    <div>
      <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">{label}</Label>
      <Select
        value={formData[field] || '__default__'}
        onValueChange={(v) => setFormData(prev => ({ ...prev, [field]: v === '__default__' ? '' : v }))}
      >
        <SelectTrigger className="h-10">
          <SelectValue placeholder={fallbackLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__default__">{fallbackLabel}</SelectItem>
          {FONT_OPTIONS.map(font => (
            <SelectItem key={font.id} value={font.id}>
              <span style={{ fontFamily: font.family }}>{font.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#E8B7B1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#333333]">Carregando editor...</p>
        </div>
      </div>
    );
  }

  if (!user?.has_subscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
        <div className="text-center max-w-md">
          <BrandLogo size="md" withText={false} iconClassName="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-[#1C1C1C] mb-4">Ative sua conta primeiro</h1>
          <p className="text-[#333333] mb-6">Para criar seu site, ative sua assinatura no painel.</p>
          <Link to="/dashboard">
            <Button className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full px-8">
              Ir para o Painel
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between z-50">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#333333] hover:text-[#1C1C1C]">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="h-6 w-px bg-slate-200" />
          <h1 className="text-lg font-bold text-[#1C1C1C]">Editor do Site</h1>
          {site && (
            <div className="hidden lg:flex items-center gap-2">
              <span className="text-sm text-[#333333]">/s/</span>
              <Input
                value={slugInput}
                onChange={(e) => setSlugInput(normalizeSlugInput(e.target.value))}
                onBlur={() => setSlugInput(prev => normalizeSlugInput(prev))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSlugUpdate();
                  }
                }}
                placeholder="nome-do-site"
                className="h-8 w-44"
                data-testid="slug-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSlugUpdate}
                disabled={updatingSlug}
                data-testid="slug-update-btn"
              >
                {updatingSlug ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Atualizar'}
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Preview mode toggle */}
          <div className="hidden md:flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-white shadow-sm' : ''}`}
              title="Desktop"
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-white shadow-sm' : ''}`}
              title="Mobile"
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
          
          {site?.slug && site.slug.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowQRModal(true)} className="hidden sm:flex">
                <QrCode className="w-4 h-4 mr-1" />
                QR
              </Button>
              <Button variant="outline" size="sm" onClick={copyLink} className="hidden sm:flex">
                <Copy className="w-4 h-4 mr-1" />
                Link
              </Button>
              <a href={`${window.location.origin}/s/${site.slug}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Abrir
                </Button>
              </a>
            </>
          )}
          <Button onClick={handleSubmit} disabled={saving} size="sm" className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {saving ? '' : 'Salvar'}
          </Button>
        </div>
      </header>

      {/* Main Content - Editor + Preview */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor Panel */}
        <div className="w-full md:w-[400px] lg:w-[450px] bg-white border-r border-slate-200 overflow-y-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="grid grid-cols-4 bg-slate-50 m-2 rounded-lg">
              <TabsTrigger value="info" className="text-xs data-[state=active]:bg-white">
                <Heart className="w-3 h-3 mr-1" />Info
              </TabsTrigger>
              <TabsTrigger value="style" className="text-xs data-[state=active]:bg-white">
                <Palette className="w-3 h-3 mr-1" />Estilo
              </TabsTrigger>
              <TabsTrigger value="images" className="text-xs data-[state=active]:bg-white">
                <Image className="w-3 h-3 mr-1" />Fotos
              </TabsTrigger>
              <TabsTrigger value="location" className="text-xs data-[state=active]:bg-white">
                <MapPin className="w-3 h-3 mr-1" />Local
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Info Tab */}
              <TabsContent value="info" className="mt-0 space-y-4">
                <div>
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Nomes do Casal</Label>
                  <Input
                    value={formData.couple_names}
                    onChange={(e) => setFormData(prev => ({ ...prev, couple_names: e.target.value }))}
                    placeholder="João & Maria"
                    className="h-10"
                    data-testid="couple-names-input"
                  />
                </div>
                
                <div>
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Endereço do Site</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center h-10 flex-1 rounded-md border border-slate-200 bg-white overflow-hidden">
                      <span className="px-2 text-[11px] text-[#333333] bg-slate-50 border-r border-slate-200">
                        {window.location.origin}/s/
                      </span>
                      <Input
                        value={slugInput}
                        onChange={(e) => setSlugInput(normalizeSlugInput(e.target.value))}
                        onBlur={() => setSlugInput(prev => normalizeSlugInput(prev))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSlugUpdate();
                          }
                        }}
                        placeholder="nome-do-site"
                        className="h-10 border-0 rounded-none focus-visible:ring-0"
                        data-testid="slug-input-mobile"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSlugUpdate}
                      disabled={!site || updatingSlug}
                      data-testid="slug-update-btn-mobile"
                    >
                      {updatingSlug ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'OK'}
                    </Button>
                  </div>
                  <p className="text-[11px] text-[#333333] mt-1">Use letras, números e hífens.</p>
                </div>
                
                <div>
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Data do Casamento</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full h-10 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.wedding_date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.wedding_date}
                        onSelect={(date) => date && setFormData(prev => ({ ...prev, wedding_date: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Nossa História</Label>
                  <Textarea
                    value={formData.story_text || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, story_text: e.target.value }))}
                    placeholder="Conte um pouco da história de vocês..."
                    className="min-h-24 text-sm"
                  />
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-3 block">Seções Visíveis</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_countdown}
                        onChange={(e) => setFormData(prev => ({ ...prev, show_countdown: e.target.checked }))}
                        className="rounded border-slate-300"
                      />
                      <Clock className="w-4 h-4 text-[#333333]" />
                      <span className="text-sm">Contador Regressivo</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_rsvp_count}
                        onChange={(e) => setFormData(prev => ({ ...prev, show_rsvp_count: e.target.checked }))}
                        className="rounded border-slate-300"
                      />
                      <Users className="w-4 h-4 text-[#333333]" />
                      <span className="text-sm">Contagem de Confirmados</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_gift_section}
                        onChange={(e) => setFormData(prev => ({ ...prev, show_gift_section: e.target.checked }))}
                        className="rounded border-slate-300"
                      />
                      <Gift className="w-4 h-4 text-[#333333]" />
                      <span className="text-sm">Lista de Presentes</span>
                    </label>
                    {formData.show_gift_section && (
                      <div className="ml-6 mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-2 block">Taxa do cartão</Label>
                        <p className="text-xs text-[#666666] mb-3">
                          Escolha se o acréscimo do cartão aparece para quem compra o presente ou se ele será descontado do valor líquido recebido pelo casal.
                        </p>
                        <div className="space-y-2">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="card_fee_responsibility"
                              value="payer"
                              checked={(formData.card_fee_responsibility || 'payer') === 'payer'}
                              onChange={(e) => setFormData(prev => ({ ...prev, card_fee_responsibility: e.target.value }))}
                              className="mt-1 border-slate-300"
                            />
                            <span className="text-sm text-[#333333]">
                              Repassar para quem compra o presente
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="card_fee_responsibility"
                              value="couple"
                              checked={formData.card_fee_responsibility === 'couple'}
                              onChange={(e) => setFormData(prev => ({ ...prev, card_fee_responsibility: e.target.value }))}
                              className="mt-1 border-slate-300"
                            />
                            <span className="text-sm text-[#333333]">
                              O casal assume essa taxa no valor recebido
                            </span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-bold text-[#1C1C1C]">Publicar Site</Label>
                      <p className="text-xs text-[#333333]">Visível para todos</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_published}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-[#E8B7B1]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                </div>

              </TabsContent>

              {/* Style Tab */}
              <TabsContent value="style" className="mt-0 space-y-4">
                <div>
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Template Visual</Label>
                  <Select
                    value={selectedTemplateForUI}
                    onValueChange={(v) => setFormData(prev => applyTemplateSelection(prev, v))}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {templateOptions.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#7E6B5F] mt-1">
                    As variações de cores ficam em <strong>Paletas Prontas</strong> logo abaixo.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-[#FAFAF9]">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => setStyleAccordionOpen(prev => ({ ...prev, banner: !prev.banner }))}
                  >
                    <div>
                      <p className="text-sm font-bold text-[#1C1C1C]">Banner Principal</p>
                      <p className="text-xs text-[#6B5B50]">Nome do casal, botao e overlay.</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#7E6B5F] transition-transform ${styleAccordionOpen.banner ? 'rotate-180' : ''}`} />
                  </button>
                  {styleAccordionOpen.banner && (
                    <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200">
                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Nome do Casal</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.banner_title_color || '#333333'}
                            onChange={(e) => setFormData(prev => ({ ...prev, banner_title_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.banner_title_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, banner_title_color: e.target.value }))}
                            placeholder="Fallback: cor dos textos"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Fonte do Nome do Casal</Label>
                        <Select
                          value={formData.banner_title_font_family || '__default__'}
                          onValueChange={(v) => setFormData(prev => ({ ...prev, banner_title_font_family: v === '__default__' ? '' : v }))}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Fallback: fonte dos títulos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__default__">Usar fonte dos títulos</SelectItem>
                            {FONT_OPTIONS.map(font => (
                              <SelectItem key={font.id} value={font.id}>
                                <span style={{ fontFamily: font.family }}>{font.name}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {renderFontSelectField('Fonte do Texto do Banner', 'banner_text_font_family', 'Usar fonte do texto')}

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Texto do Banner</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.banner_text_color || '#333333'}
                            onChange={(e) => setFormData(prev => ({ ...prev, banner_text_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.banner_text_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, banner_text_color: e.target.value }))}
                            placeholder="Fallback: cor dos textos"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Botao Fundo</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.confirm_button_background_color || '#001F3F'}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.confirm_button_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_background_color: e.target.value }))}
                            placeholder="Fallback: cor principal"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Botao Texto</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.confirm_button_text_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_text_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.confirm_button_text_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_text_color: e.target.value }))}
                            placeholder="Fallback: contraste automatico"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor de Fundo do Banner</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.banner_section_background_color || '#F3F8F4'}
                            onChange={(e) => setFormData(prev => ({ ...prev, banner_section_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.banner_section_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, banner_section_background_color: e.target.value }))}
                            placeholder="Fallback: fundo do template"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">
                          Opacidade do Overlay ({formData.hero_overlay_opacity}%)
                        </Label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={formData.hero_overlay_opacity}
                          onChange={(e) => setFormData(prev => ({ ...prev, hero_overlay_opacity: parseInt(e.target.value, 10) }))}
                          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-[#FAFAF9]">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => setStyleAccordionOpen(prev => ({ ...prev, story: !prev.story }))}
                  >
                    <div>
                      <p className="text-sm font-bold text-[#1C1C1C]">Nossa História</p>
                      <p className="text-xs text-[#6B5B50]">Título, fonte e fundo da seção.</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#7E6B5F] transition-transform ${styleAccordionOpen.story ? 'rotate-180' : ''}`} />
                  </button>
                  {styleAccordionOpen.story && (
                    <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200">
                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Título</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.story_title_color || '#333333'}
                            onChange={(e) => setFormData(prev => ({ ...prev, story_title_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.story_title_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, story_title_color: e.target.value }))}
                            placeholder="Fallback: cor dos textos"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Fonte do Título</Label>
                        <Select
                          value={formData.story_title_font_family || '__default__'}
                          onValueChange={(v) => setFormData(prev => ({ ...prev, story_title_font_family: v === '__default__' ? '' : v }))}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Fallback: fonte dos títulos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__default__">Usar fonte dos títulos</SelectItem>
                            {FONT_OPTIONS.map(font => (
                              <SelectItem key={font.id} value={font.id}>
                                <span style={{ fontFamily: font.family }}>{font.name}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {renderFontSelectField('Fonte do Texto', 'story_text_font_family', 'Usar fonte do texto')}

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor de Fundo da Seção</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.story_section_background_color || '#FAF8F2'}
                            onChange={(e) => setFormData(prev => ({ ...prev, story_section_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.story_section_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, story_section_background_color: e.target.value }))}
                            placeholder="Fallback: fundo do template"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-[#FAFAF9]">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => setStyleAccordionOpen(prev => ({ ...prev, gallery: !prev.gallery }))}
                  >
                    <div>
                      <p className="text-sm font-bold text-[#1C1C1C]">Nossa Galeria</p>
                      <p className="text-xs text-[#6B5B50]">Cores de título, ícone e fundo da seção.</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#7E6B5F] transition-transform ${styleAccordionOpen.gallery ? 'rotate-180' : ''}`} />
                  </button>
                  {styleAccordionOpen.gallery && (
                    <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200">
                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Título</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gallery_title_color || '#1C1C1C'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gallery_title_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gallery_title_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gallery_title_color: e.target.value }))}
                            placeholder="Fallback: cor principal"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Ícone</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gallery_icon_color || '#C8A97E'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gallery_icon_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gallery_icon_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gallery_icon_color: e.target.value }))}
                            placeholder="Fallback: cor secundária"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor de Fundo da Seção</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gallery_section_background_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gallery_section_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gallery_section_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gallery_section_background_color: e.target.value }))}
                            placeholder="Fallback: branco"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-[#FAFAF9]">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => setStyleAccordionOpen(prev => ({ ...prev, gifts: !prev.gifts }))}
                  >
                    <div>
                      <p className="text-sm font-bold text-[#1C1C1C]">Lista de Presentes</p>
                      <p className="text-xs text-[#6B5B50]">Cores da seção, cards, ícone e botão.</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#7E6B5F] transition-transform ${styleAccordionOpen.gifts ? 'rotate-180' : ''}`} />
                  </button>
                  {styleAccordionOpen.gifts && (
                    <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200">
                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Título</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gift_title_color || '#1C1C1C'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_title_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gift_title_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_title_color: e.target.value }))}
                            placeholder="Fallback: cor principal"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Texto</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gift_text_color || '#333333'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_text_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gift_text_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_text_color: e.target.value }))}
                            placeholder="Fallback: cor dos textos"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Ícone</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gift_icon_color || '#C8A97E'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_icon_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gift_icon_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_icon_color: e.target.value }))}
                            placeholder="Fallback: cor secundária"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor de Fundo da Seção</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gift_section_background_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_section_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gift_section_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_section_background_color: e.target.value }))}
                            placeholder="Fallback: branco"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor de Fundo do Card</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gift_card_background_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_card_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gift_card_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_card_background_color: e.target.value }))}
                            placeholder="Fallback: fundo do card"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Preço</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gift_price_color || '#C8A97E'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_price_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gift_price_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_price_color: e.target.value }))}
                            placeholder="Fallback: cor secundária"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Botão</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gift_button_background_color || '#1C1C1C'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_button_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gift_button_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_button_background_color: e.target.value }))}
                            placeholder="Fallback: cor principal"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Texto do Botão</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.gift_button_text_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_button_text_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.gift_button_text_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, gift_button_text_color: e.target.value }))}
                            placeholder="Fallback: contraste automático"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-[#FAFAF9]">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => setStyleAccordionOpen(prev => ({ ...prev, location: !prev.location }))}
                  >
                    <div>
                      <p className="text-sm font-bold text-[#1C1C1C]">Localização</p>
                      <p className="text-xs text-[#6B5B50]">Cores de título, texto e fundo da seção.</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#7E6B5F] transition-transform ${styleAccordionOpen.location ? 'rotate-180' : ''}`} />
                  </button>
                  {styleAccordionOpen.location && (
                    <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200">
                      {renderFontSelectField('Fonte do Título', 'location_title_font_family', 'Usar fonte dos títulos')}
                      {renderFontSelectField('Fonte do Texto / Endereço', 'location_text_font_family', 'Usar fonte do texto')}

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Título</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.location_title_color || '#1C1C1C'}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_title_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.location_title_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_title_color: e.target.value }))}
                            placeholder="Fallback: cor principal"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Texto / Endereço</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.location_text_color || '#333333'}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_text_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.location_text_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_text_color: e.target.value }))}
                            placeholder="Fallback: cor dos textos"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor de Fundo da Seção</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.location_section_background_color || '#F8F6F4'}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_section_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.location_section_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_section_background_color: e.target.value }))}
                            placeholder="Fallback: fundo do template"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor de Fundo do Card</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.location_card_background_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_card_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.location_card_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_card_background_color: e.target.value }))}
                            placeholder="Fallback: fundo do card"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor dos Ícones</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.location_icon_color || '#1C1C1C'}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_icon_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.location_icon_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, location_icon_color: e.target.value }))}
                            placeholder="Fallback: cor do titulo"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-[#FAFAF9]">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => setStyleAccordionOpen(prev => ({ ...prev, rsvp: !prev.rsvp }))}
                  >
                    <div>
                      <p className="text-sm font-bold text-[#1C1C1C]">Confirme sua Presença</p>
                      <p className="text-xs text-[#6B5B50]">Cores do botão e fundo da seção.</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#7E6B5F] transition-transform ${styleAccordionOpen.rsvp ? 'rotate-180' : ''}`} />
                  </button>
                  {styleAccordionOpen.rsvp && (
                    <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200">
                      {renderFontSelectField('Fonte do Título', 'rsvp_title_font_family', 'Usar fonte dos títulos')}
                      {renderFontSelectField('Fonte do Texto', 'rsvp_text_font_family', 'Usar fonte do texto')}

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor Botão Fundo</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.confirm_button_background_color || '#001F3F'}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.confirm_button_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_background_color: e.target.value }))}
                            placeholder="Fallback: cor principal"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor Botão Texto</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.confirm_button_text_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_text_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.confirm_button_text_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_text_color: e.target.value }))}
                            placeholder="Fallback: contraste automtico"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor Botão Borda</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.confirm_button_border_color || '#001F3F'}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_border_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.confirm_button_border_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirm_button_border_color: e.target.value }))}
                            placeholder="Fallback: cor principal"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor de Fundo da Seção</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.rsvp_section_background_color || '#1C1C1C'}
                            onChange={(e) => setFormData(prev => ({ ...prev, rsvp_section_background_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.rsvp_section_background_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, rsvp_section_background_color: e.target.value }))}
                            placeholder="Fallback: fundo do template"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor dos Textos da Seção</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.rsvp_text_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, rsvp_text_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.rsvp_text_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, rsvp_text_color: e.target.value }))}
                            placeholder="Fallback: cor dos textos"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Cor do Ícone</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.rsvp_icon_color || '#FFFFFF'}
                            onChange={(e) => setFormData(prev => ({ ...prev, rsvp_icon_color: e.target.value }))}
                            className="w-10 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={formData.rsvp_icon_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, rsvp_icon_color: e.target.value }))}
                            placeholder="Fallback: cor dos textos da seção"
                            className="h-10 flex-1 text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-2 block">Paletas Prontas</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { primary: '#1C1C1C', secondary: '#C8A97E', name: 'Clássico' },
                      { primary: '#2D3436', secondary: '#E17055', name: 'Moderno' },
                      { primary: '#6C5CE7', secondary: '#FD79A8', name: 'Romântico' },
                      { primary: '#00B894', secondary: '#FDCB6E', name: 'Tropical' },
                      { primary: '#2C3E50', secondary: '#E74C3C', name: 'Elegante' },
                      { primary: '#8E44AD', secondary: '#F39C12', name: 'Luxo' },
                      { primary: '#1ABC9C', secondary: '#E74C3C', name: 'Vibrante' },
                      { primary: '#34495E', secondary: '#1ABC9C', name: 'Fresh' }
                    ].map((palette, idx) => (
                      <button
                        key={idx}
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          primary_color: palette.primary,
                          secondary_color: palette.secondary,
                          text_color: getAutoContrastTextColor(palette.primary, prev.text_color || '#1A1A1A')
                        }))}
                        className="p-2 rounded-lg border hover:border-slate-400 transition-colors"
                        title={palette.name}
                      >
                        <div className="flex gap-1">
                          <div className="w-4 h-4 rounded" style={{ background: palette.primary }}></div>
                          <div className="w-4 h-4 rounded" style={{ background: palette.secondary }}></div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Images Tab */}
              <TabsContent value="images" className="mt-0 space-y-4">
                <div>
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-2 block">Imagem Principal (Hero)</Label>
                  {formData.hero_image ? (
                    <div className="relative">
                      <img src={formData.hero_image} alt="Hero" className="w-full h-32 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, hero_image: '' }))}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => heroInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#E8B7B1]"
                    >
                      <Upload className="w-8 h-8 text-[#333333] mx-auto mb-2" />
                      <p className="text-sm text-[#333333]">Clique para upload</p>
                    </div>
                  )}
                  <input
                    ref={heroInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    className="hidden"
                    onChange={handleHeroFileChange}
                  />
                  <Input
                    value={formData.hero_image}
                    onChange={(e) => setFormData(prev => ({ ...prev, hero_image: e.target.value }))}
                    placeholder="Ou cole a URL da imagem"
                    className="h-9 mt-2 text-xs"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-bold text-[#333333] uppercase">Galeria de Fotos</Label>
                    <span className="text-xs text-[#333333]">{formData.gallery_images.length} fotos</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {formData.gallery_images.map((img, idx) => (
                      <div key={idx} className="relative aspect-square">
                        <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(idx)}
                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => galleryInputRef.current?.click()}
                      disabled={uploading}
                      className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#E8B7B1]"
                    >
                      {uploading ? (
                        <RefreshCw className="w-5 h-5 text-[#333333] animate-spin" />
                      ) : (
                        <Plus className="w-5 h-5 text-[#333333]" />
                      )}
                    </button>
                  </div>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    className="hidden"
                    onChange={handleGalleryFileChange}
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold text-[#333333] uppercase mb-1 block">Posição da Galeria</Label>
                  <Select value={formData.gallery_position} onValueChange={(v) => setFormData(prev => ({ ...prev, gallery_position: v }))}>
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="after_hero">Após o Hero</SelectItem>
                      <SelectItem value="after_story">Após Nossa História</SelectItem>
                      <SelectItem value="before_gifts">Antes dos Presentes</SelectItem>
                      <SelectItem value="end">No Final</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              {/* Location Tab */}
              <TabsContent value="location" className="mt-0 space-y-4">
                <label className="flex items-start gap-3 rounded-lg border border-[#E8B7B1]/45 bg-[#FDF8F7] px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(formData.same_location)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        same_location: checked,
                        reception_location: checked ? prev.ceremony_location : prev.reception_location,
                        reception_address: checked ? prev.ceremony_address : prev.reception_address,
                        reception_time: checked ? prev.ceremony_time : prev.reception_time
                      }));
                    }}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <span className="text-xs leading-5 text-[#333333]">
                    Cerimônia e recepção no mesmo local.
                    <span className="block text-[11px] text-[#7E6B5F]">
                      Quando ativado, exibimos um único bloco de localização no site.
                    </span>
                  </span>
                </label>

                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-[#1C1C1C] flex items-center gap-2">
                    <Heart className="w-4 h-4 text-[#C8A97E]" />
                    Cerimônia
                  </h4>
                  <Input
                    value={formData.ceremony_location}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        ceremony_location: e.target.value,
                        reception_location: prev.same_location ? e.target.value : prev.reception_location
                      }))
                    }
                    placeholder="Nome do local"
                    className="h-9 text-sm"
                  />
                  <Input
                    value={formData.ceremony_address}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        ceremony_address: e.target.value,
                        reception_address: prev.same_location ? e.target.value : prev.reception_address
                      }))
                    }
                    placeholder="Endereço completo"
                    className="h-9 text-sm"
                  />
                  <Input
                    value={formData.ceremony_time}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        ceremony_time: e.target.value,
                        reception_time: prev.same_location ? e.target.value : prev.reception_time
                      }))
                    }
                    placeholder="Horário (ex: 16:00)"
                    className="h-9 text-sm"
                  />
                </div>

                {!formData.same_location ? (
                  <div className="space-y-3 pt-4 border-t border-slate-100">
                    <h4 className="text-sm font-bold text-[#1C1C1C] flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#C8A97E]" />
                      Recepção
                    </h4>
                    <Input
                      value={formData.reception_location}
                      onChange={(e) => setFormData(prev => ({ ...prev, reception_location: e.target.value }))}
                      placeholder="Nome do local"
                      className="h-9 text-sm"
                    />
                    <Input
                      value={formData.reception_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, reception_address: e.target.value }))}
                      placeholder="Endereço completo"
                      className="h-9 text-sm"
                    />
                    <Input
                      value={formData.reception_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, reception_time: e.target.value }))}
                      placeholder="Horário (ex: 19:00)"
                      className="h-9 text-sm"
                    />
                  </div>
                ) : (
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-xs text-[#7E6B5F]">Recepção vinculada à cerimônia.</p>
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Preview Panel */}
        <div className="hidden md:flex flex-1 bg-slate-200 items-center justify-center p-6 overflow-hidden">
          <div 
            className={`bg-white shadow-2xl rounded-lg overflow-hidden transition-all duration-300 ${
              previewMode === 'mobile' ? 'w-[375px] h-[667px]' : 'w-full max-w-4xl h-full'
            }`}
          >
            <div className="w-full h-full overflow-y-auto" ref={previewRef}>
              {/* Live Preview Content */}
              {useStructuredPreview ? (
                <WeddingTemplateRenderer
                  templateId={selectedTemplateId}
                  site={previewStructuredSite}
                  theme={previewStructuredTheme}
                  daysUntil={daysUntil}
                  weddingDateLabel={previewWeddingDateLabel}
                  locationCards={previewLocationCards}
                  availableGifts={previewDisplayGifts}
                  galleryPosition={previewGalleryPosition}
                  isTemplatePreview
                  onTemplatePreviewRedirect={() => {}}
                  onOpenRsvp={() => {}}
                  onGiftClick={() => {}}
                  onOpenGallery={() => {}}
                  showCountdown={formData.show_countdown !== false}
                  showGiftSection={formData.show_gift_section !== false}
                  showRsvpCount={formData.show_rsvp_count !== false}
                  confirmedGuests={TEMPLATE_PREVIEW_CONTENT.confirmed_guests}
                />
              ) : (
              <div style={{ fontFamily: getBodyFont() }}>
                {/* Hero Section */}
                <section className={`relative flex items-center justify-center ${
                  isHtmlExactTemplate
                    ? 'h-[76vh] min-h-[600px]'
                    : isDestinationPraiaTemplate
                    ? 'h-[44vh] min-h-[360px]'
                    : isModernBlackTieTemplate
                    ? 'h-[60vh] min-h-[460px]'
                    : 'h-[50vh] min-h-[400px]'
                }`}>
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${previewHeroImage})` }}
                  />
                  <div 
                    className="absolute inset-0"
                    style={{ 
                      background: isHtmlExactTemplate
                        ? 'linear-gradient(180deg, rgba(224,225,228,0.42) 0%, rgba(225,221,216,0.32) 55%, rgba(248,248,248,0.18) 100%)'
                        : isPremiumWeddingTemplate
                        ? 'linear-gradient(180deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.34) 55%, rgba(255,255,255,0.12) 100%)'
                        : `linear-gradient(to bottom, ${formData.primary_color}${Math.round(formData.hero_overlay_opacity * 2.55).toString(16).padStart(2, '0')}, ${formData.primary_color}cc)`
                    }}
                  />
                  {(isPremiumWeddingTemplate || isHtmlExactTemplate) && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{ background: previewHeroOverlayTint }}
                    />
                  )}
                  <div className={`relative z-10 px-6 ${isBotanicalGardenTemplate ? 'w-full max-w-5xl' : 'text-center'}`}>
                    {!isPremiumWeddingTemplate && !isHtmlExactTemplate && (
                      <img
                        src={brandMark}
                        alt="Virei Noiva"
                        className="w-8 h-8 mx-auto mb-4 object-contain"
                      />
                    )}
                    <h1 
                      className={isHtmlExactTemplate ? 'text-[clamp(2.2rem,6vw,4.8rem)] font-light tracking-[0.07em] uppercase mb-4' : isPremiumWeddingTemplate ? 'text-4xl md:text-6xl font-light tracking-[0.14em] uppercase mb-4' : 'text-3xl md:text-5xl font-bold mb-4'}
                      style={{ 
                        fontFamily: previewBannerTitleFont,
                        color: previewBannerTitleColor,
                        textShadow: isPremiumWeddingTemplate || isHtmlExactTemplate ? '0 2px 7px rgba(0,0,0,0.25)' : 'none'
                      }}
                    >
                      {(isPremiumWeddingTemplate || isHtmlExactTemplate)
                        ? String(previewCoupleNames).toUpperCase()
                        : previewCoupleNames}
                    </h1>
                    <p
                      className={isPremiumWeddingTemplate ? 'text-sm mb-6 uppercase tracking-[0.24em]' : isHtmlExactTemplate ? 'text-lg md:text-3xl mb-0 tracking-[0.04em] font-light' : 'text-lg mb-6'}
                      style={{ color: isHtmlExactTemplate ? 'rgba(244,247,251,0.9)' : previewTextColor, fontFamily: previewBannerTextFont }}
                    >
                      {isHtmlExactTemplate
                        ? htmlExactDateLabel
                        : format(previewDisplayDate, "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                    </p>
                    
                    {!isPremiumWeddingTemplate && !isHtmlExactTemplate && formData.show_countdown && (
                      <div className="flex justify-center gap-4 mb-6">
                        <div 
                          className="w-16 h-16 rounded-xl flex items-center justify-center"
                          style={{ background: `${formData.secondary_color}30` }}
                        >
                          <span className="text-2xl font-bold" style={{ color: formData.secondary_color }}>
                            {daysUntil}
                          </span>
                        </div>
                        <span className="self-end text-sm pb-2" style={{ color: `${previewTextColor}CC` }}>dias</span>
                      </div>
                    )}

                    {isModernBlackTieTemplate && (
                      <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div className="rounded-xl border border-white/25 bg-black/40 px-3 py-2 text-left">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">Contagem</p>
                          <p className="text-lg font-semibold text-white">{daysUntil} dias</p>
                        </div>
                        <div className="rounded-xl border border-white/25 bg-black/40 px-3 py-2 text-left">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">Cerimônia</p>
                          <p className="text-sm font-semibold text-white">{previewCeremonyTime || '--:--'}</p>
                        </div>
                        <div className="rounded-xl border border-white/25 bg-black/40 px-3 py-2 text-left">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">Local</p>
                          <p className="text-sm font-semibold text-white truncate">{previewCeremonyLocation || previewReceptionLocation || 'A confirmar'}</p>
                        </div>
                      </div>
                    )}

                    {!isPremiumWeddingTemplate && !isHtmlExactTemplate && (
                      <Button
                        className="rounded-full px-8"
                        style={{
                          background: previewConfirmButtonBackgroundColor,
                          color: previewConfirmButtonTextColor,
                          borderColor: previewConfirmButtonBorderColor,
                          borderWidth: 1,
                          borderStyle: 'solid'
                        }}
                      >
                        Confirmar Presença
                      </Button>
                    )}
                  </div>
                </section>

                {isDestinationPraiaTemplate && (
                  <section className="px-6 py-6 bg-[#F7FBFE]">
                    <div className="max-w-4xl mx-auto grid gap-3 md:grid-cols-3">
                      <div className="rounded-xl border border-[#D7E7F2] bg-white px-4 py-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C8AA7]">Cerimônia</p>
                        <p className="text-sm mt-1 font-semibold text-[#1D3F53]">{previewCeremonyTime || '--:--'}</p>
                      </div>
                      <div className="rounded-xl border border-[#D7E7F2] bg-white px-4 py-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C8AA7]">Recepção</p>
                        <p className="text-sm mt-1 font-semibold text-[#1D3F53]">{previewReceptionTime || '--:--'}</p>
                      </div>
                      <div className="rounded-xl border border-[#D7E7F2] bg-white px-4 py-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C8AA7]">Contagem</p>
                        <p className="text-sm mt-1 font-semibold text-[#1D3F53]">{daysUntil} dias</p>
                      </div>
                    </div>
                  </section>
                )}

                {previewHtmlHeaderPanel}
                {previewHtmlDivider}
                {previewGalleryPosition === 'after_hero' && previewGallerySection}

                {/* Story Section */}
                {previewStoryText && (
                  <section className="py-12 px-6" style={{ background: isHtmlExactTemplate ? '#FFFFFF' : isMinimalClassicTemplate ? '#FAFAFA' : formData.primary_color }}>
                    <div className={`mx-auto text-center ${isMinimalClassicTemplate ? 'max-w-xl' : 'max-w-2xl'}`}>
                      <h2 
                        className="text-2xl font-bold mb-6"
                        style={{
                          fontFamily: previewStoryTitleFont,
                          color: previewStoryTitleColor,
                          letterSpacing: isHtmlExactTemplate ? '0.08em' : undefined,
                          textTransform: isHtmlExactTemplate ? 'uppercase' : 'none',
                          fontWeight: isHtmlExactTemplate ? 500 : undefined
                        }}
                      >
                        {isHtmlExactTemplate ? 'Bem-vindos ao nosso casamento!' : 'Nossa História'}
                      </h2>
                      <p
                        className="leading-relaxed whitespace-pre-line"
                        style={{ color: previewStoryBodyColor, fontFamily: previewStoryBodyFont }}
                      >
                        {previewStoryText}
                      </p>
                    </div>
                  </section>
                )}

                {previewGalleryPosition === 'after_story' && previewGallerySection}

                {/* Location Section */}
                {previewLocationCards.length > 0 && (
                  <section className="py-12 px-6" style={{ background: isMinimalClassicTemplate ? '#FCFCFC' : '#F6F1EF' }}>
                    <div className="max-w-4xl mx-auto">
                      <div className="text-center mb-8">
                        <MapPin className="w-8 h-8 mx-auto mb-3" style={{ color: formData.secondary_color }} />
                        <h2 
                          className="text-2xl font-bold"
                          style={{ fontFamily: previewLocationTitleFont, color: previewLocationTitleColor }}
                        >
                          Localização
                        </h2>
                      </div>
                      <div className={`grid gap-6 ${previewLocationCards.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-2xl mx-auto'}`}>
                        {previewLocationCards.map((item) => {
                          const Icon = item.icon;
                          return (
                            <div key={item.key} className="bg-white rounded-xl p-6 text-center shadow-sm">
                              <div
                                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                                style={{ background: item.iconBackground }}
                              >
                                <Icon className="w-6 h-6 text-white" />
                              </div>
                              <h3 className="font-bold mb-1" style={{ color: previewLocationTitleColor, fontFamily: previewLocationTitleFont }}>{item.title}</h3>
                              {item.location ? (
                                <p className="text-sm" style={{ color: previewLocationTextColor, fontFamily: previewLocationTextFont }}>{item.location}</p>
                              ) : null}
                              {item.address ? (
                                <p className="text-xs mt-1" style={{ color: previewLocationTextColor, fontFamily: previewLocationTextFont }}>{item.address}</p>
                              ) : null}
                              {item.time ? (
                                <p className="text-sm mt-2" style={{ color: previewLocationTextColor, fontFamily: previewLocationTextFont }}>
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {item.time}
                                </p>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                )}

                {previewGalleryPosition === 'before_gifts' && previewGallerySection}

                {/* Gift Section Preview */}
                {formData.show_gift_section && (
                  <section className="py-12 px-6" style={{ background: previewGiftSectionBackgroundColor }}>
                    <div className="max-w-4xl mx-auto text-center">
                      <Gift className="w-8 h-8 mx-auto mb-3" style={{ color: previewGiftIconColor }} />
                      <h2 
                        className="text-2xl font-bold mb-2"
                        style={{ fontFamily: getHeadingFont(), color: previewGiftTitleColor }}
                      >
                        Lista de Presentes
                      </h2>
                      <p className="text-xs uppercase tracking-[0.18em]" style={{ color: previewGiftTextColor }}>
                        {previewCardFeeResponsibility === 'couple'
                          ? 'Cartão sem acréscimo para o convidado'
                          : 'Taxa do cartão repassada ao convidado'}
                      </p>
                      {previewDisplayGifts.length > 0 ? (
                        <div className={`grid gap-4 mt-6 text-left ${isMinimalClassicTemplate ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                          {previewDisplayGifts.map((gift) => (
                            <div key={gift.gift_id} className="rounded-xl p-4 border border-slate-100" style={{ background: previewGiftCardBackgroundColor }}>
                              <p className="font-semibold text-sm" style={{ color: previewGiftTitleColor }}>{gift.name}</p>
                              <p className="text-sm mt-1" style={{ color: previewGiftPriceColor }}>
                                {`R$ ${Number(gift.price || 0).toFixed(2).replace('.', ',')}`}
                              </p>
                              <div className="mt-4 flex justify-end">
                                <span className="inline-flex rounded-full px-4 py-2 text-xs font-semibold" style={{ background: previewGiftButtonBackgroundColor, color: previewGiftButtonTextColor }}>
                                  Presentear
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm" style={{ color: previewGiftTextColor }}>Seus presentes aparecerão aqui</p>
                      )}
                    </div>
                  </section>
                )}

                {previewGalleryPosition === 'end' && previewGallerySection}

                {/* Footer */}
                <footer className="py-6 text-center" style={{ background: formData.primary_color }}>
                  <img src={brandMark} alt="Virei Noiva" className="w-5 h-5 mx-auto mb-2 object-contain" />
                  <p className="text-sm" style={{ color: previewTextColor }}>{previewCoupleNames}</p>
                </footer>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && site && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowQRModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-[#1C1C1C] mb-4 text-center">QR Code do Site</h3>
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-white rounded-xl shadow-lg">
                <QRCodeSVG value={getSiteUrl()} size={180} level="H" includeMargin={true} />
              </div>
            </div>
            <p className="text-center text-[#333333] text-sm mb-4">Use nos seus convites físicos</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyLink} className="flex-1 rounded-full">
                <Copy className="w-4 h-4 mr-1" />
                Copiar
              </Button>
              <Button onClick={() => setShowQRModal(false)} className="flex-1 bg-[#E8B7B1] text-white rounded-full">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}






